/**
 * OneTrust Cookie Consent Utilities
 *
 * Provides utilities for checking and subscribing to consent state changes.
 * Works with OneTrust SDK and the HudlConsentUpdated custom event
 * dispatched by OptanonWrapper.
 */

// Consent category IDs from OneTrust
// NOTE: These must match the constants in _ConsentScripts.cshtml
export const CONSENT_CATEGORIES = {
  STRICTLY_NECESSARY: 'C0001',
  PERFORMANCE: 'C0002',
  FUNCTIONAL: 'C0003',
  TARGETING: 'C0004',
};

/**
 * Checks if consent has been given for a specific category.
 * @param {string} category - Consent category ID (e.g., 'C0002')
 * @returns {boolean} - True if consent is granted for the category
 */
export function hasConsentFor(category) {
  if (typeof window === 'undefined' || !window.OnetrustActiveGroups) {
    return false;
  }
  return window.OnetrustActiveGroups.split(',').filter(g => g !== '' && g.indexOf('C000') === 0).indexOf(category) !== -1;
}

/**
 * Checks if consent has been determined (user has made a choice or has existing consent).
 * @returns {boolean} - True if OnetrustActiveGroups is set
 */
export function isConsentDetermined() {
  return typeof window !== 'undefined' && typeof window.OnetrustActiveGroups === 'string';
}

export function mayUpgradeVendorConsent() {
  return typeof window === 'undefined' || window.__mayUpgradeVendorConsent !== false;
}

const CONSENT_STATE_FALLBACK = { hasPerformance: false, hasTargeting: false, ready: true };

/**
 * Returns a promise that resolves with the __consentState object
 * once _ConsentScriptLoader.cshtml has populated it.
 * Falls back to a safe default (no consent) after 3 seconds.
 * @returns {Promise<{hasPerformance: boolean, hasTargeting: boolean, ready: boolean}>}
 */
export function getConsentState() {
  if (window.__consentState && window.__consentState.ready) {
    return Promise.resolve(window.__consentState);
  }
  return new Promise(resolve => {
    if (window.__consentState && window.__consentState.ready) {
      resolve(window.__consentState);
      return;
    }
    function onReady() {
      window.removeEventListener('ConsentScriptsReady', onReady);
      resolve(window.__consentState);
    }
    window.addEventListener('ConsentScriptsReady', onReady);
    setTimeout(() => {
      window.removeEventListener('ConsentScriptsReady', onReady);
      resolve(window.__consentState || CONSENT_STATE_FALLBACK);
    }, 3000);
  });
}

/**
 * Subscribes to consent changes from OneTrust.
 * The callback is called whenever the user updates their consent preferences.
 * @param {function(string[]): void} callback - Function called with array of active group IDs
 * @returns {function(): void} - Unsubscribe function
 */
export function subscribeToConsentChanges(callback) {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const handler = event => {
    callback(event.detail);
  };

  window.addEventListener('HudlConsentUpdated', handler);

  return () => {
    window.removeEventListener('HudlConsentUpdated', handler);
  };
}
